import CustomPreloader from '@components/CustomPreloader'
import MaintenanceModal from '@components/MaintenanceModal'
import { configuration, useLogErrorRequest } from 'mmfintech-checkout-commons'
import { Button } from 'mmfintech-portal-commons'
import { Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Toaster } from 'react-hot-toast'
import { withTranslation } from 'react-i18next'
import { Redirect, Route, type RouteProps, BrowserRouter as Router, Switch } from 'react-router-dom'
import styled, { ThemeProvider } from 'styled-components'
import ErrorIcon from './assets/icons/error-icon.svg?react'
import { ContentWrapper } from './components'
import routes from './routes'
import settings from './settings'
import theme from './theme'

const AppInner = () => {
  useEffect(() => {
    configuration.createBackendConfig(settings.backendForLocalhost)
    // configuration.createCardGatewayConfig(settings.)
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <ContentWrapper>
          <Switch>
            {routes.map(route => {
              const { path, exact, redirect, component } = route

              if (redirect) {
                return (
                  <Route key={path} exact={exact} path={path}>
                    <Redirect to={redirect} />
                  </Route>
                )
              }

              if (component) {
                return <CustomRoute key={path} path={path} exact={exact} component={component} />
              }

              return null
            })}
          </Switch>

          <MaintenanceModal />
        </ContentWrapper>

        <Toaster
          position='top-right'
          containerStyle={{
            top: '10rem'
          }}
          toastOptions={{
            className: '',
            style: {
              color: '#000000',
              fontFamily: 'inherit',
              fontSize: '1.4rem',
              fontStyle: 'normal',
              padding: '1.5rem',
              borderRadius: '0'
            },
            success: {}
          }}
        />
      </Router>
    </ThemeProvider>
  )
}

const ErrorFallback = ({ resetErrorBoundary }) => {
  return (
    <AlertWrapper>
      <Alert>
        <ErrorIcon />
        <p>An unexpected error occurred</p>
        {resetErrorBoundary ? (
          <Button color='alternative' onClick={resetErrorBoundary} text='Go back to the home page' />
        ) : null}
      </Alert>
    </AlertWrapper>
  )
}

const ThisApp = withTranslation()(AppInner)

const App = () => {
  const { logErrorRequest } = useLogErrorRequest()

  return (
    <Suspense
      fallback={
        <PreloaderWrapper>
          <CustomPreloader />
        </PreloaderWrapper>
      }
    >
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error: Error, info: { componentStack: string }) =>
          logErrorRequest(error.toString(), info?.componentStack)
        }
        onReset={() => window.location.replace('/')}
      >
        <ThisApp />
      </ErrorBoundary>
    </Suspense>
  )
}

export default App

const PreloaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100vw;
  height: 100vh;
`

const AlertWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: 8rem;
`

const Alert = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;

  max-width: 50rem;
  width: 100%;
  border-radius: 1rem;
  padding: 3rem;

  text-align: center;
  font-size: 16px;

  img {
    width: 66px;
    height: 38px;
  }
  .button {
    width: 100%;
    max-width: 30rem;
  }
`

const CustomRoute = ({ component: Component, ...rest }: RouteProps) => (
  <Route {...rest} render={props => <Component {...props} />} />
)
